import { DEFAULT_PAGE_MARGINS_MM, resolvePageBox } from './htmlToPdf.js';

/** Matches `htmlDocumentToPdfBlob` html2canvas scale — pixels beyond this never appear sharper in the PDF. */
const PDF_EXPORT_SCALE = 2;

/**
 * Resize/re-encode embedded data:image URLs to the pixel budget actually used in the PDF,
 * and strip junk attributes from those <img> tags.
 *
 * PDF content is A4 minus margins (~640px CSS). Export uses scale=2, so ~1280 CSS-independent
 * pixels is the useful ceiling for a full-width image.
 *
 * @param {string} html
 * @param {{
 *   margins?: { top?: number, right?: number, bottom?: number, left?: number },
 *   maxWidth?: number,
 *   quality?: number,
 *   minBytesToTouch?: number,
 * }} [opts]
 * @returns {Promise<string>}
 */
export async function compressHtmlDataImages(html, opts = {}) {
  if (typeof html !== 'string' || !html.includes('data:image/')) {
    return html;
  }

  const box = resolvePageBox(opts.margins ?? DEFAULT_PAGE_MARGINS_MM);
  const maxUsefulPx = Math.max(
    320,
    Math.round(opts.maxWidth ?? box.contentWidth * PDF_EXPORT_SCALE),
  );
  const quality = opts.quality ?? 0.82;
  // Still re-encode smaller bitmaps when the payload is bloated (PNG metadata / unused alpha).
  const minBytesToTouch = opts.minBytesToTouch ?? 48_000;

  const imgRe = /<img\b[^>]*>/gi;
  /** @type {{ tag: string, start: number, end: number }[]} */
  const tags = [];
  let m;
  while ((m = imgRe.exec(html)) !== null) {
    tags.push({ tag: m[0], start: m.index, end: m.index + m[0].length });
  }
  if (tags.length === 0) {
    return html;
  }

  /** @type {Map<string, Promise<string|null>>} */
  const encodeCache = new Map();

  const optimized = await Promise.all(
    tags.map(async ({ tag }) => {
      const cleaned = scrubImgTag(tag);
      const src = readAttr(cleaned, 'src');
      if (!src || !src.startsWith('data:image/')) {
        return cleaned === tag ? null : cleaned;
      }

      const displayCssPx = estimateDisplayCssWidth(cleaned, box.contentWidth);
      const targetPx = Math.max(1, Math.min(maxUsefulPx, Math.round(displayCssPx * PDF_EXPORT_SCALE)));

      const cacheKey = `${targetPx}|${quality}|${src.length}|${src.slice(0, 64)}|${src.slice(-32)}`;
      let pending = encodeCache.get(cacheKey);
      if (!pending) {
        pending = compressDataUrl(src, targetPx, quality, minBytesToTouch);
        encodeCache.set(cacheKey, pending);
      }
      const nextSrc = await pending;
      if (!nextSrc || nextSrc === src) {
        return cleaned === tag ? null : cleaned;
      }
      return writeAttr(cleaned, 'src', nextSrc);
    }),
  );

  let out = '';
  let cursor = 0;
  let changed = false;
  for (let i = 0; i < tags.length; i += 1) {
    const { tag, start, end } = tags[i];
    out += html.slice(cursor, start);
    const next = optimized[i];
    if (next && next !== tag) {
      out += next;
      changed = true;
    } else {
      out += tag;
    }
    cursor = end;
  }
  out += html.slice(cursor);
  return changed ? out : html;
}

/**
 * Drop AI/placeholder junk and conflicting size attrs; keep layout hints that help PDF.
 * @param {string} tag
 */
function scrubImgTag(tag) {
  let next = tag;
  next = next.replace(/\sdata-ai-generate=(["'])[^"']*\1/gi, '');
  next = next.replace(/\sdata-ai-img=(["'])[^"']*\1/gi, '');
  next = next.replace(/\ssrc=(["'])data-ai-placeholder\1/gi, '');
  // height on <img> fights max-width:100% / height:auto and can blow layout.
  next = next.replace(/\sheight=(["']?)[^"'\s>]+\1/gi, '');
  // Normalize style: keep max-width, drop empty/noise declarations.
  const style = readAttr(next, 'style');
  if (style) {
    const cleanedStyle = minifyImgStyle(style);
    next = cleanedStyle
      ? writeAttr(next, 'style', cleanedStyle)
      : next.replace(/\sstyle=(["'])[^"']*\1/gi, '');
  }
  next = next.replace(/\s{2,}/g, ' ').replace(/\s+>/, '>');
  return next;
}

/**
 * @param {string} style
 */
function minifyImgStyle(style) {
  /** @type {string[]} */
  const keep = [];
  for (const part of style.split(';')) {
    const bit = part.trim();
    if (!bit) continue;
    const idx = bit.indexOf(':');
    if (idx <= 0) continue;
    const prop = bit.slice(0, idx).trim().toLowerCase();
    const val = bit.slice(idx + 1).trim();
    if (!val || val === 'null' || val === 'undefined') continue;
    if (
      prop === 'max-width'
      || prop === 'width'
      || prop === 'height'
      || prop === 'object-fit'
      || prop === 'display'
      || prop === 'margin'
      || prop === 'margin-top'
      || prop === 'margin-bottom'
      || prop === 'margin-left'
      || prop === 'margin-right'
    ) {
      if (prop === 'height' && /^auto$/i.test(val)) {
        keep.push('height:auto');
        continue;
      }
      keep.push(`${prop}:${val}`);
    }
  }
  if (!keep.some((k) => k.startsWith('max-width:'))) {
    keep.unshift('max-width:100%');
  }
  if (!keep.some((k) => k.startsWith('height:'))) {
    keep.push('height:auto');
  }
  return keep.join(';');
}

/**
 * Estimate on-page CSS width for an <img> (capped by PDF content width).
 * @param {string} tag
 * @param {number} contentWidthPx
 */
function estimateDisplayCssWidth(tag, contentWidthPx) {
  const style = readAttr(tag, 'style') || '';
  const widthAttr = readAttr(tag, 'width');

  const styleWidth = style.match(/(?:^|;)\s*width\s*:\s*([\d.]+)\s*(px|%)?/i);
  if (styleWidth) {
    const n = Number(styleWidth[1]);
    if (Number.isFinite(n) && n > 0) {
      if ((styleWidth[2] || 'px').startsWith('%')) {
        return Math.min(contentWidthPx, (contentWidthPx * n) / 100);
      }
      return Math.min(contentWidthPx, n);
    }
  }

  const maxWidth = style.match(/(?:^|;)\s*max-width\s*:\s*([\d.]+)\s*(px|%)?/i);
  if (maxWidth) {
    const n = Number(maxWidth[1]);
    if (Number.isFinite(n) && n > 0) {
      if ((maxWidth[2] || 'px').startsWith('%')) {
        return Math.min(contentWidthPx, (contentWidthPx * n) / 100);
      }
      return Math.min(contentWidthPx, n);
    }
  }

  if (widthAttr) {
    const n = Number(String(widthAttr).replace(/px$/i, ''));
    if (Number.isFinite(n) && n > 0) {
      return Math.min(contentWidthPx, n);
    }
  }

  // Default: images are max-width:100% of the PDF content column.
  return contentWidthPx;
}

/**
 * @param {string} tag
 * @param {string} name
 */
function readAttr(tag, name) {
  const re = new RegExp(`\\s${name}=(["'])([\\s\\S]*?)\\1`, 'i');
  const m = tag.match(re);
  return m ? m[2] : null;
}

/**
 * @param {string} tag
 * @param {string} name
 * @param {string} value
 */
function writeAttr(tag, name, value) {
  const re = new RegExp(`\\s${name}=(["'])[\\s\\S]*?\\1`, 'i');
  if (re.test(tag)) {
    return tag.replace(re, ` ${name}="${value}"`);
  }
  return tag.replace(/^<img\b/i, `<img ${name}="${value}"`);
}

/**
 * @param {string} dataUrl
 * @param {number} maxWidthPx
 * @param {number} quality
 * @param {number} minBytesToTouch
 * @returns {Promise<string|null>}
 */
function compressDataUrl(dataUrl, maxWidthPx, quality, minBytesToTouch = 48_000) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const naturalW = Math.max(1, img.naturalWidth || img.width);
        const naturalH = Math.max(1, img.naturalHeight || img.height);
        const scale = Math.min(1, maxWidthPx / naturalW);
        const w = Math.max(1, Math.round(naturalW * scale));
        const h = Math.max(1, Math.round(naturalH * scale));

        // Already within PDF pixel budget and not bloated — keep as-is.
        if (scale === 1 && dataUrl.length < minBytesToTouch) {
          resolve(dataUrl);
          return;
        }

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }

        const needsAlpha = dataUrl.startsWith('data:image/png') || dataUrl.startsWith('data:image/webp');
        if (needsAlpha) {
          ctx.clearRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, w, h);
          if (canvasHasTransparency(ctx, w, h)) {
            const png = canvas.toDataURL('image/png');
            resolve(png.length < dataUrl.length || scale < 1 ? png : dataUrl);
            return;
          }
        }

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        const jpeg = canvas.toDataURL('image/jpeg', quality);
        resolve(jpeg.length < dataUrl.length || scale < 1 ? jpeg : dataUrl);
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} w
 * @param {number} h
 */
function canvasHasTransparency(ctx, w, h) {
  try {
    const sampleW = Math.min(w, 64);
    const sampleH = Math.min(h, 64);
    const data = ctx.getImageData(0, 0, sampleW, sampleH).data;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 250) return true;
    }
  } catch {
    return false;
  }
  return false;
}
